import { Directive, ElementRef, Input, NgZone, type OnDestroy, type OnInit, Renderer2, inject } from '@angular/core';
import ResizeObserver from '@juggle/resize-observer';

@Directive({ selector: '[fitText]' })
export class FitTextDirective implements OnInit, OnDestroy {
    private _elementRef = inject(ElementRef);
    private renderer = inject(Renderer2);
    private _ngZone = inject(NgZone);

    @Input() compression: number = 1;
    @Input() minFontSize: number = 0;
    @Input() maxFontSize: number = Number.POSITIVE_INFINITY;

    private newlines: number;
    private calcSize = 10;

    private parentResizeObserver!: ResizeObserver;
    private childrenMutateObserver!: MutationObserver;

    private currentParentSize!: { width: number; height: number };

    constructor() {
        this.newlines = this._elementRef.nativeElement.childElementCount > 0 ? this._elementRef.nativeElement.childElementCount : 1;
    }

    ngOnInit() {
        /**
         * Running mutation and resize observer outside angular as change detection is not needed
         * and zone was endlessly refreshing
         */
        this._ngZone.runOutsideAngular(() => {
            this.parentResizeObserver = new ResizeObserver(entries => {
                const entry = entries[0];
                if (!entry) return;
                this.currentParentSize = { width: entry.contentRect.width, height: entry.contentRect.height };
                this.setFontSize(this.currentParentSize);
            });

            this.childrenMutateObserver = new MutationObserver(mutations => {
                mutations.forEach(() => {
                    if (!this.currentParentSize) return;
                    this.setFontSize(this.currentParentSize);
                });
            });

            this.parentResizeObserver.observe(this._elementRef.nativeElement.parentElement);
            this.childrenMutateObserver.observe(this._elementRef.nativeElement, {
                subtree: true,
                characterData: true
            });
        });
    }

    ngOnDestroy() {
        this.parentResizeObserver.disconnect();
        this.childrenMutateObserver.disconnect();
    }

    private setFontSize = (size: { width: number; height: number }): void => {
        const currentDisplay = window.getComputedStyle(this._elementRef.nativeElement)['display'];
        const currentLineHeight = window.getComputedStyle(this._elementRef.nativeElement)['line-height' as any];

        // reset to default size to use as benchmark for calculate new font size
        this.setStyles(this.calcSize, 1, 'inline-block');

        // set new
        this.setStyles(this.calculateNewFontSize(size), currentLineHeight, currentDisplay);
    };

    private calculateNewFontSize = (size: { width: number; height: number }): number => {
        const ratioWidth = (this.calcSize * this.newlines) / this._elementRef.nativeElement.offsetWidth / this.newlines;
        const ratioHeight = (this.calcSize * this.newlines) / this._elementRef.nativeElement.offsetHeight / this.newlines;

        const maxWidthFontSize = (size.width - 6) * ratioWidth * this.compression;
        const maxHeightFontSize = (size.height - 6) * ratioHeight * this.compression;

        return Math.max(Math.min(Math.min(maxWidthFontSize, maxHeightFontSize), this.maxFontSize), this.minFontSize);
    };

    private setStyles = (fontSize: number, lineHeight: number | string, display: string): void => {
        this.renderer.setStyle(this._elementRef.nativeElement, 'fontSize', fontSize.toString() + 'px');
        this.renderer.setStyle(this._elementRef.nativeElement, 'lineHeight', lineHeight.toString());
        this.renderer.setStyle(this._elementRef.nativeElement, 'display', display);
    };
}
